import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EmailIcon from "@mui/icons-material/Email";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useSearchParams } from "react-router";
import { LoadingBox } from "@/components/ui";
import {
  useBillingPlans,
  useCreateCheckout,
  useCreatePortal,
  useSubscription,
  useUpdatePlan,
} from "../../hooks/use-billing";
import type { BillingPlan } from "../../types/billing";

const formatPrice = (cents: number, currency: string) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  });
  return formatter.format(cents / 100);
};

interface PlanCardProps {
  plan: BillingPlan;
  isCurrentPlan: boolean;
  currentPlanPrice: number;
  onSelect: (isUpgrade: boolean) => void;
  onDowngrade: () => void;
  isLoading: boolean;
  hasStripeSubscription: boolean;
  cancelAt?: string | null;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isCurrentPlan,
  currentPlanPrice,
  onSelect,
  onDowngrade,
  isLoading,
  hasStripeSubscription,
  cancelAt,
}) => {
  const isUpgrade = plan.price_cents > currentPlanPrice;
  const isDowngrade = plan.price_cents < currentPlanPrice && !plan.is_free;
  const isCancelled = !!cancelAt;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        border: isCurrentPlan ? "2px solid" : "1px solid",
        borderColor: isCurrentPlan ? "primary.main" : "divider",
        position: "relative",
        zIndex: 999,
      }}
    >
      {isCurrentPlan && (
        <Chip
          label="Current Plan"
          color="primary"
          size="small"
          sx={{
            position: "absolute",
            zIndex: 1000,
            top: -4,
            right: 12,
          }}
        />
      )}
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {plan.name}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="primary" mt={1}>
              {plan.is_free ? "Free" : `${formatPrice(plan.price_cents, plan.currency)}/mo`}
            </Typography>
          </Box>

          <Divider />

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon fontSize="small" color="success" />
              <Typography variant="body2">
                {plan.tokens_per_month.toLocaleString()} credits/month
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon fontSize="small" color="success" />
              <Typography variant="body2">
                {plan.max_users === -1
                  ? "Unlimited users"
                  : plan.max_users === 1
                    ? "1 user"
                    : `Up to ${plan.max_users} users`}
              </Typography>
            </Stack>
          </Stack>

          <Box mt="auto">
            {isCurrentPlan ? (
              <Button variant="outlined" disabled fullWidth>
                Current Plan
              </Button>
            ) : plan.is_free ? (
              isCancelled ? (
                <Button variant="outlined" disabled fullWidth>
                  Active after {new Date(cancelAt!).toLocaleDateString()}
                </Button>
              ) : hasStripeSubscription ? (
                <Button variant="outlined" fullWidth onClick={onDowngrade}>
                  Downgrade
                </Button>
              ) : (
                <Button variant="outlined" disabled fullWidth>
                  Default Plan
                </Button>
              )
            ) : (
              <Button
                variant={isUpgrade ? "contained" : "outlined"}
                fullWidth
                onClick={() => onSelect(isUpgrade)}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} /> : null}
              >
                {isUpgrade ? "Upgrade" : isDowngrade ? "Downgrade" : "Select"}
              </Button>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const EnterprisePlanCard: React.FC<{
  planName: string;
  status?: string | null;
  currentPeriodEnd?: string | null;
}> = ({ planName, status, currentPeriodEnd }) => {
  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <BusinessIcon color="primary" fontSize="large" />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Enterprise Plan
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You&apos;re on a custom enterprise plan managed by our team.
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body1" fontWeight="medium">
                Plan:
              </Typography>
              <Chip label={planName} color="primary" />
              {status && (
                <Chip
                  label={status}
                  size="small"
                  color={status === "active" ? "success" : "warning"}
                />
              )}
            </Stack>

            {currentPeriodEnd && (
              <Typography variant="body2" color="text.secondary">
                Current period ends: {new Date(currentPeriodEnd).toLocaleDateString()}
              </Typography>
            )}

            <Alert severity="info" icon={<BusinessIcon />}>
              Your enterprise plan includes custom features, dedicated support, and tailored usage
              limits. Self-service billing changes are disabled for enterprise accounts.
            </Alert>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              Need to make changes to your plan?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contact our team to discuss plan adjustments, add-ons, or any billing questions.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EmailIcon />}
              href="mailto:t@desplega.ai?subject=Enterprise%20Plan%20Inquiry"
              component="a"
            >
              Contact Sales
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const BillingTab: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  // Confirmation modal state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    plan: BillingPlan | null;
    isUpgrade: boolean;
  }>({ open: false, plan: null, isUpgrade: true });

  // Check for checkout result
  React.useEffect(() => {
    const checkoutResult = searchParams.get("checkout");
    if (checkoutResult === "success") {
      enqueueSnackbar("Subscription created successfully!", {
        variant: "success",
      });
    } else if (checkoutResult === "cancelled") {
      enqueueSnackbar("Checkout was cancelled.", {
        variant: "info",
      });
    }
  }, [searchParams, enqueueSnackbar]);

  const { data: plansData, isLoading: isLoadingPlans } = useBillingPlans();
  const { data: subscription, isLoading: isLoadingSubscription, refetch } = useSubscription();
  const { checkout, isPending: isCheckingOut } = useCreateCheckout();
  const { openPortal, isPending: isOpeningPortal } = useCreatePortal();
  const { updatePlan, isPending: isUpdatingPlan } = useUpdatePlan();

  const handleOpenConfirmDialog = (plan: BillingPlan, isUpgrade: boolean) => {
    // If user doesn't have a subscription yet, go straight to checkout
    if (!subscription?.has_stripe_subscription) {
      checkout(plan.key);
      return;
    }
    setConfirmDialog({ open: true, plan, isUpgrade });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, plan: null, isUpgrade: true });
  };

  const handleConfirmPlanChange = async () => {
    if (!confirmDialog.plan) return;

    try {
      await updatePlan(confirmDialog.plan.key);
      enqueueSnackbar(`Plan ${confirmDialog.isUpgrade ? "upgraded" : "downgraded"} successfully!`, {
        variant: "success",
      });
      refetch();
      handleCloseConfirmDialog();
    } catch {
      enqueueSnackbar("Failed to update plan. Please try again.", {
        variant: "error",
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      await openPortal();
    } catch {
      enqueueSnackbar("Failed to open billing portal. Please try again.", {
        variant: "error",
      });
    }
  };

  if (isLoadingPlans || isLoadingSubscription) {
    return <LoadingBox />;
  }

  // Enterprise plans get a special view - no self-service options
  if (subscription?.plan_type === "enterprise") {
    return (
      <EnterprisePlanCard
        planName={subscription.plan_name || "Enterprise"}
        status={subscription.status}
        currentPeriodEnd={subscription.current_period_end}
      />
    );
  }

  const currentPlanKey = subscription?.plan_key || "free";
  const currentPlan = plansData?.plans.find((p) => p.key === currentPlanKey);
  const currentPlanPrice = currentPlan?.price_cents || 0;

  return (
    <>
      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{confirmDialog.isUpgrade ? "Upgrade" : "Downgrade"} Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.isUpgrade ? (
              <>
                You are about to upgrade to the <strong>{confirmDialog.plan?.name}</strong> plan at{" "}
                <strong>
                  {confirmDialog.plan &&
                    formatPrice(confirmDialog.plan.price_cents, confirmDialog.plan.currency)}
                  /month
                </strong>
                .
                <br />
                <br />
                Your card will be charged the prorated amount for the remainder of this billing
                period.
              </>
            ) : (
              <>
                You are about to downgrade to the <strong>{confirmDialog.plan?.name}</strong> plan
                at{" "}
                <strong>
                  {confirmDialog.plan &&
                    formatPrice(confirmDialog.plan.price_cents, confirmDialog.plan.currency)}
                  /month
                </strong>
                .
                <br />
                <br />
                You will receive a prorated credit for the unused portion of your current plan.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmPlanChange}
            variant="contained"
            color={confirmDialog.isUpgrade ? "primary" : "warning"}
            disabled={isUpdatingPlan}
            startIcon={isUpdatingPlan ? <CircularProgress size={16} /> : null}
          >
            {confirmDialog.isUpgrade ? "Upgrade" : "Downgrade"}
          </Button>
        </DialogActions>
      </Dialog>

      <Stack spacing={3}>
        {/* Current Subscription Status */}
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <CreditCardIcon color="primary" />
                  <Box>
                    <Typography variant="h6">Current Subscription</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {subscription?.plan_name || "Free"} Plan
                      {subscription?.status && (
                        <Chip
                          label={subscription.status}
                          size="small"
                          color={
                            subscription.status === "active"
                              ? "success"
                              : subscription.status === "trialing"
                                ? "info"
                                : "warning"
                          }
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                  </Box>
                </Stack>

                {subscription?.has_stripe_subscription && (
                  <Button
                    variant="outlined"
                    onClick={handleManageSubscription}
                    disabled={isOpeningPortal}
                    startIcon={isOpeningPortal ? <CircularProgress size={16} /> : <OpenInNewIcon />}
                  >
                    Manage Subscription
                  </Button>
                )}
              </Stack>

              {subscription?.current_period_end && (
                <Typography variant="body2" color="text.secondary">
                  {subscription.cancel_at ? "Access until: " : "Next billing date: "}
                  {new Date(
                    subscription.cancel_at || subscription.current_period_end,
                  ).toLocaleDateString()}
                </Typography>
              )}

              {subscription?.cancel_at && (
                <Alert severity="warning">
                  Your subscription is scheduled for cancellation on{" "}
                  {new Date(subscription.cancel_at).toLocaleDateString()}. You will continue to have
                  access until then. Click &quot;Manage Subscription&quot; to reactivate.
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Plan Selection */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available Plans
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Choose the plan that best fits your needs. Upgrade or downgrade at any time.
            </Typography>

            <Grid container spacing={3}>
              {plansData?.plans.map((plan) => (
                <Grid key={plan.key} size={{ xs: 12, sm: 6, md: 4 }}>
                  <PlanCard
                    plan={plan}
                    isCurrentPlan={plan.key === currentPlanKey}
                    currentPlanPrice={currentPlanPrice}
                    onSelect={(isUpgrade) => handleOpenConfirmDialog(plan, isUpgrade)}
                    onDowngrade={handleManageSubscription}
                    isLoading={isCheckingOut || isUpdatingPlan}
                    hasStripeSubscription={subscription?.has_stripe_subscription || false}
                    cancelAt={subscription?.cancel_at}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* FAQ/Help Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Billing FAQ
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  How do credits work?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Credits are used for test runs, discovery runs, AI steps, and other operations.
                  Your credit allocation resets at the start of each billing period.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Can I change my plan?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Yes! You can upgrade or downgrade at any time. Changes take effect immediately,
                  and we&apos;ll prorate the charges.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Need more credits or users?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Contact us at <a href="mailto:t@desplega.ai">t@desplega.ai</a> for custom
                  enterprise plans tailored to your needs.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};

export default BillingTab;
